import _ from 'lodash';

import request from 'api/request';
import {
  handleError,
  reloadPortalSettings,
  reloadSessionWithPortalName,
} from 'helpers';
import { Paging } from 'helpers/constants';

import { asyncLoad, asyncPost } from './.';
import { changePageSize } from './tableActions';
import {
  portalActionTypes,
  myAccountActionTypes,
  userActionTypes,
  featureFlagSettingsActionTypes
} from './actionTypes';

export const loadAddOns = () => dispatch => {
  const url = `/addons`;

  return request({ url })
    .then(data => {
      const results =
        data && data.data && data.data.results ? data.data.results : [];

      results.sort((a, b) => a.title.localeCompare(b.title));

      const addOns = results.map(item => ({
        ...item,
        title: item.title ? item.title : 'None',
        description: item.description ? item.description : 'None',
      }));

      dispatch({type: portalActionTypes.LOAD_ADDON_SUCCESS, addOns});
    })
    .catch(error => {
      handleError('Unable to load Add Ons', error, dispatch);
    });
};

export const startup = () => async dispatch => {
  try {
    const response = await request({url: '/web/startup'});
    const returnedResults = {...response.data};

    //* portal settings
    const settings = response.data.settings || [];
    const settingsObj = {};
    settings.forEach(item => {
      settingsObj[item.name] = item;
    });

    returnedResults.modifiedSettings = settingsObj;

    dispatch({
      type: portalActionTypes.LOAD_PORTAL_SETTINGS_SUCCESS,
      portalSettings: settingsObj,
    });

    //* portal
    dispatch({
      type: portalActionTypes.LOAD_PORTAL_SUCCESS,
      portalData: response.data.portal,
    });

    //* dictionary
    const dictionary = response.data.dictionary || [];
    const newDictionary = {};

    if (dictionary.length > 0) {
      for (let i = 0; i < dictionary.length; i++) {
        const item = dictionary[i];

        newDictionary[item.key] = {
          value: item.value,
          pluralValue: item.pluralValue,
        };
      }
    }

    returnedResults.modifiedDictionary = newDictionary;

    dispatch({
      type: portalActionTypes.LOAD_DICTIONARY_SUCCESS,
      dictionary: newDictionary,
    });

    //* timezone
    dispatch({
      field: 'timezone',
      type: userActionTypes.TIMEZONE.COMPLETE,
      payload: response.data.timeZone,
      stateField: 'timezone',
    });

    //* user preferences
    const prefs = _.keyBy(response.data.preferences, 'key');

    returnedResults.modifiedPreferences = prefs;

    dispatch({
      field: 'userPreferences',
      type: userActionTypes.USER_PREFERENCE.COMPLETE,
      payload: prefs,
      stateField: 'preferences',
    });
    dispatch(
      changePageSize(
        prefs.tlDashboardPageSize
          ? Number(prefs.tlDashboardPageSize.value)
          : Paging.initialPageSize,
      ),
    );

    //* user
    dispatch({
      type: myAccountActionTypes.ACCOUNT_LOAD_SUCCESS,
      account: response.data.user,
    });

    dispatch({
      type: featureFlagSettingsActionTypes.FEATURE_FLAG_SETTINGS,
      featureFlagSettings: response.data.featureFlagSettings,
    });

    return response.data;
  } catch (error) {
    //* portal settings
    dispatch({ type: portalActionTypes.LOAD_PORTAL_SETTINGS_COMPLETE });

    //* dictionary
    dispatch({ type: portalActionTypes.LOAD_DICTIONARY_COMPLETE });

    //* timezone
    dispatch({
      type: userActionTypes.TIMEZONE.LOADING,
      field: 'timezone',
      loading: false,
    });

    //* user preferences
    dispatch({
      type: userActionTypes.USER_PREFERENCE.LOADING,
      field: 'userPreferences',
      loading: false,
    });

    //* user
    dispatch({
      type: myAccountActionTypes.ACCOUNT_LOAD_ERROR,
      field: 'account',
      loading: false,
    });

    //* display error
    handleError('Unable to load the Portal', error, dispatch);
  }
};

export const loadPortalSettings = () => dispatch => {
  const url = `/settings`;

  return request({url})
    .then(res => {
      const settings = (res && res.data) || [];
      const settingsObj = {};
      settings.forEach(item => {
        settingsObj[item.name] = item;
      });
      dispatch({
        type: portalActionTypes.LOAD_PORTAL_SETTINGS_SUCCESS,
        portalSettings: settingsObj,
      });

      return settingsObj;
    })
    .catch(error => {
      handleError('Unable to load the Portal Settings', error, dispatch);
      dispatch({type: portalActionTypes.LOAD_PORTAL_SETTINGS_COMPLETE});
    });
};

export const resetMAPIPortalSettings = () => dispatch => {
  const url = '/portalsetting/reset';

  return request({url, method: 'POST', useMAPI: true})
    .then(() => {
      dispatch(loadPortalSettings());
    })
    .catch(error => {
      handleError('Unable to reset Portal Settings', error, dispatch);
    });
};

export const updateAddOn = (
  name,
  title,
  settingValue,
  {inv = false, multiInv = false} = {},
  consolidate = true,
  callback = null,
  noToaster = false,
) => (dispatch, getState) => {
  const state = getState();
  const value = settingValue ? 'true' : 'false';
  const url = multiInv
    ? '/addons/usemultilocationinventory'
    : inv
      ? '/addons/useinventorytracking'
      : '/settings';
  const body = multiInv
    ? {name, value, consolidate}
    : inv
      ? {name, value, DeleteInventory: consolidate}
      : {portalId: state.user.tokenData.portal_id, name, value};

  return request({
    url,
    method: multiInv || inv ? 'PUT' : 'POST',
    body,
  })
    .then(setting => {
      dispatch({
        type: portalActionTypes.UPDATE_ADDON_SUCCESS,
        setting: setting ? setting.data : {name, value},
      });

      if (window.toastr !== undefined && !noToaster) {
        window.toastr.success(
          `Successfully turned ${
            settingValue ? 'on' : 'off'
          } the Add On ${title}.`,
          '',
          window.MHD.Global.Toastr.success,
        );
      }

      dispatch(refreshPortalCache());

      if (name.indexOf('QBDesktop2') > -1) {
        if (settingValue) {
          window.MHD.UI.BootstrapMenu.showQBIcon();
        } else if (settingValue === false) {
          window.MHD.UI.BootstrapMenu.hideQBIcon();
        }
      }

      if (typeof callback === 'function') callback();
    })
    .catch(error => {
      handleError('Unable to update the Add On', error, dispatch);
    });
};

export const loadPortal = () => dispatch => {
  const url = '';

  return request({url})
    .then(result =>
      dispatch({
        type: portalActionTypes.LOAD_PORTAL_SUCCESS,
        portalData: result.data,
      }),
    )
    .catch(error => {
      handleError('Unable to load the Portal', error, dispatch);
    });
};

export const updatePortalNameAndAddress = portalInfo => dispatch => {
  const body = [
    {op: 'replace', path: '/name', value: portalInfo.name},
    {op: 'replace', path: '/address1', value: portalInfo.address1},
    {op: 'replace', path: '/address2', value: portalInfo.address2},
    {op: 'replace', path: '/city', value: portalInfo.city},
    {op: 'replace', path: '/state', value: portalInfo.state},
    {op: 'replace', path: '/zip', value: portalInfo.zip},
  ];

  return request({
    url: '/',
    method: 'PATCH',
    body,
  })
    .then(result => {
      reloadSessionWithPortalName(portalInfo.name);
      dispatch({
        type: portalActionTypes.UPDATE_PORTAL_SUCCESS,
        portal: result.data,
      });
    })
    .catch(error => {
      handleError('Unable to update the Portal', error, dispatch);
    });
};

export const updatePortalSetting = (name, value) => (dispatch, getState) => {
  const state = getState();
  const url = `/settings`;
  const body = {
    PortalId: state.user.tokenData.portal_id,
    Name: name,
    Value: value,
  };

  return request({
    url,
    method: 'POST',
    body,
  })
    .then(results => {
      reloadPortalSettings();
      dispatch(resetMAPIPortalSettings());
      dispatch({
        type: portalActionTypes.UPDATE_PORTAL_SETTINGS_SUCCESS,
        portal: results.data,
      });
      return true;
    })
    .catch(error => {
      handleError(
        `Unable to update the Portal Setting: ${name}`,
        error,
        dispatch,
      );
      return false;
    });
};

export const uploadPortalLogo = fileData => dispatch => {
  const url = '/portals/UploadCompanyLogo';
  const body = {
    FileData: fileData ? fileData.replace('data:image/png;base64,', '') : '',
    FileName: 'MyCompanyLogo.png', // required field but name doesn't matter
  };

  return request({
    url,
    method: 'POST',
    body,
    useMAPI: true,
  }).catch(error => {
    handleError('Unable to upload the Logo', error, dispatch);
  });
};

export const loadDictionary = () => dispatch => {
  const url = `/dictionary`;

  return request({url})
    .then(data => {
      const dictionary =
        data && data.data && data.data.results ? data.data.results : [];
      const newDictionary = {};

      if (dictionary.length > 0) {
        for (let i = 0; i < dictionary.length; i++) {
          const item = dictionary[i];

          newDictionary[item.key] = {
            value: item.value,
            pluralValue: item.pluralValue,
          };
        }
      }

      dispatch({
        type: portalActionTypes.LOAD_DICTIONARY_SUCCESS,
        dictionary: newDictionary,
      });
    })
    .catch(error => {
      handleError('Unable to load the Dictionary', error, dispatch);
      dispatch({type: portalActionTypes.LOAD_DICTIONARY_COMPLETE});
    });
};

export const loadFusebillUrl = () => dispatch => {
  const url = '/billing/login';

  return request({url})
    .then(data => {
      const url = data && data.data ? data.data.url : null;

      if (!url) {
        throw new Error();
      }

      return url;
    })
    .catch(error =>
      handleError(
        'Unable to load the FuseBill Self Service URL',
        error,
        dispatch,
      ),
    );
};

export const refreshPortalCache = () => dispatch => {
  if (
    window.MHD.UI.Navigation !== undefined &&
    window.MHD.UI.QuickMenu !== undefined
  ) {
    window.MHD.UI.QuickMenu.refreshQuickMenu();
    window.MHD.UI.Navigation.loadMenu();
  }

  reloadPortalSettings();
  dispatch(resetMAPIPortalSettings());

  return Promise.resolve();
};

export const loadTemplates = () => dispatch =>
  asyncLoad({
    dispatch,
    title: 'template',
    url: '/templates',
  });

export const updateTemplate = ({portalTemplateId, templateName, template}) => (
  dispatch,
  getState,
) => {
  const {portalId} = getState().user.tokenData;
  return asyncPost({
    body: {
      PortalTemplateId: portalTemplateId,
      PortalId: portalId,
      TemplateName: templateName,
      Template: template,
    },
    dispatch,
    params: {method: 'PUT'},
    successMessage: `Template saved`,
    title: 'template',
    url: `/templates/${portalTemplateId}`,
  });
};



// WEBPACK FOOTER //
// ./src/actions/portalActions.js